import React, { useState } from 'react';
import AdminLayout from '../components/layout/AdminLayout';
import { 
  FiMessageSquare, FiSearch, FiFilter, FiSend, 
  FiPaperclip, FiUser, FiInfo, FiAlertCircle, 
  FiCheck, FiClock, FiMoreVertical 
} from 'react-icons/fi';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot' | 'admin';
  timestamp: Date;
  read: boolean;
}

interface Chat {
  id: string;
  user: {
    id: string;
    name: string;
    avatar?: string;
    status: 'online' | 'offline' | 'away';
  };
  lastMessage: {
    text: string;
    timestamp: Date;
    sender: 'user' | 'bot' | 'admin';
  };
  unreadCount: number;
  expertRequested: boolean;
  assignedTo?: string;
}

type FilterType = 'all' | 'unread' | 'expert' | 'assigned';

const AdminChats: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChat, setSelectedChat] = useState<string | null>('chat1');
  const [messageText, setMessageText] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  
  // Mock data for chats
  const chats: Chat[] = [
    {
      id: 'chat1',
      user: {
        id: 'user1',
        name: 'Sarah Johnson',
        status: 'online'
      },
      lastMessage: {
        text: 'I need help with my investment portfolio please.',
        timestamp: new Date('2023-07-10T14:30:00'),
        sender: 'user'
      },
      unreadCount: 2,
      expertRequested: true
    },
    {
      id: 'chat2',
      user: {
        id: 'user2',
        name: 'Michael Chen',
        status: 'online'
      },
      lastMessage: {
        text: 'Thanks for your help with setting up my account.',
        timestamp: new Date('2023-07-10T13:45:00'),
        sender: 'user'
      },
      unreadCount: 0,
      expertRequested: false
    },
    {
      id: 'chat3',
      user: {
        id: 'user3',
        name: 'Emily Rodriguez',
        status: 'offline'
      },
      lastMessage: {
        text: "I'm having trouble with my transactions, they're not showing up.",
        timestamp: new Date('2023-07-10T12:15:00'),
        sender: 'user'
      },
      unreadCount: 1,
      expertRequested: true,
      assignedTo: 'Aadi'
    },
    {
      id: 'chat4',
      user: {
        id: 'user4',
        name: 'David Kim',
        status: 'away'
      },
      lastMessage: {
        text: 'Is there a way to download my transaction history?',
        timestamp: new Date('2023-07-09T10:30:00'),
        sender: 'user'
      },
      unreadCount: 0,
      expertRequested: false
    },
    {
      id: 'chat5',
      user: {
        id: 'user5',
        name: 'Jessica Lee',
        status: 'online'
      },
      lastMessage: {
        text: 'What are the fees for international transfers?',
        timestamp: new Date('2023-07-08T16:20:00'),
        sender: 'user'
      },
      unreadCount: 0,
      expertRequested: false
    }
  ];
  
  // Mock messages for the selected chat
  const mockMessages: Record<string, Message[]> = {
    chat1: [
      {
        id: '1',
        text: 'Hello, I need help with my investment portfolio.',
        sender: 'user',
        timestamp: new Date('2023-07-10T14:30:00'),
        read: true
      },
      {
        id: '2',
        text: "I have some stocks and bonds, but I'm not sure if my allocation is right for my age.",
        sender: 'user',
        timestamp: new Date('2023-07-10T14:31:00'),
        read: true
      },
      {
        id: '3',
        text: "Hello! I'd be happy to help you with your investment portfolio. To better assist you, could you provide some information about your current age, retirement goals, and risk tolerance?",
        sender: 'bot',
        timestamp: new Date('2023-07-10T14:32:00'),
        read: true
      },
      {
        id: '4',
        text: "I'm 35 and hoping to retire by 60. I'd say my risk tolerance is moderate. I want growth but I'm concerned about the current market volatility.",
        sender: 'user',
        timestamp: new Date('2023-07-10T14:35:00'),
        read: true
      },
      {
        id: '5',
        text: "Thank you for sharing that information. At 35 with retirement at 60, you have a 25-year investment horizon, which generally allows for a growth-oriented portfolio with moderate risk.\n\nWould you like me to connect you with one of our financial advisors for a personalized portfolio review?",
        sender: 'bot',
        timestamp: new Date('2023-07-10T14:37:00'),
        read: true
      },
      {
        id: '6',
        text: "Yes, I think that would be helpful. I'd like to speak with an expert about this.",
        sender: 'user',
        timestamp: new Date('2023-07-10T14:40:00'),
        read: false
      },
      {
        id: '7',
        text: "Great! I'll connect you with one of our financial advisors. They typically respond within 1-2 business hours. Is there anything specific you'd like them to focus on during your consultation?",
        sender: 'bot',
        timestamp: new Date('2023-07-10T14:41:00'),
        read: false
      }
    ],
    chat3: [
      {
        id: '1',
        text: "Hi, I'm having trouble with my transactions. They're not showing up in my account history.",
        sender: 'user',
        timestamp: new Date('2023-07-10T12:15:00'),
        read: true
      },
      {
        id: '2',
        text: "I made a transfer yesterday but it's still not appearing.",
        sender: 'user',
        timestamp: new Date('2023-07-10T12:16:00'),
        read: true
      },
      {
        id: '3',
        text: "Hello! I understand you're having issues with missing transactions. I'd be happy to look into this for you. Could you please tell me when you made the transfer and from which account?",
        sender: 'bot',
        timestamp: new Date('2023-07-10T12:17:00'),
        read: true
      },
      {
        id: '4',
        text: "It was yesterday around 3 PM. I transferred from my checking account to my savings account. The money has left my checking but hasn't appeared in savings.",
        sender: 'user',
        timestamp: new Date('2023-07-10T12:19:00'),
        read: true
      },
      {
        id: '5',
        text: "Thank you for that information. I see the issue now. There appears to be a system delay affecting some internal transfers. Our team is aware and working to resolve this. Transfers are secure and should appear within 24 hours. Would you like me to have someone look specifically at your transfer?",
        sender: 'bot',
        timestamp: new Date('2023-07-10T12:21:00'),
        read: true
      },
      {
        id: '6',
        text: "Yes please, I would appreciate if someone could check on it. This is quite urgent as I need to make a payment from my savings account tomorrow.",
        sender: 'user',
        timestamp: new Date('2023-07-10T12:23:00'),
        read: true
      },
      {
        id: '7',
        text: "I understand the urgency. I'll connect you with one of our transaction specialists right away. Please hold while I transfer your chat.",
        sender: 'bot',
        timestamp: new Date('2023-07-10T12:24:00'),
        read: true
      },
      {
        id: '8',
        text: "Hello Emily, I'm Aadi from the transaction support team. I've looked into your transfer and can confirm it's been processed. The delay you're experiencing is due to a sync issue. I'll expedite this right now, and the funds should appear in your savings within the next 30 minutes. I'll stay on this chat until we confirm it's resolved.",
        sender: 'admin',
        timestamp: new Date('2023-07-10T12:30:00'),
        read: false
      }
    ]
  };
  
  // Get messages for current chat
  const currentMessages = selectedChat ? (mockMessages[selectedChat] || []) : [];
  
  const filteredChats = chats.filter(chat => {
    // Apply search filter
    if (searchQuery && !chat.user.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Apply category filter
    if (filter === 'expert' && !chat.expertRequested) {
      return false;
    }
    if (filter === 'unread' && chat.unreadCount === 0) {
      return false;
    }
    if (filter === 'assigned' && !chat.assignedTo) {
      return false;
    }
    
    return true;
  });
  
  const handleSendMessage = () => {
    if (messageText.trim() === '' || !selectedChat) return;
    // In a real app, this would send the message to an API
    console.log('Sending message:', messageText);
    setMessageText('');
  };
  
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  const formatDate = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return `Today, ${formatTime(date)}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday, ${formatTime(date)}`;
    } else {
      return `${date.toLocaleDateString()} ${formatTime(date)}`;
    }
  };
  
  return (
    <AdminLayout>
      <div className="h-screen bg-background overflow-hidden">
        <div className="container mx-auto px-0 py-0 h-full">
          <div className="flex h-full">
            {/* Chat list sidebar */}
            <div className="w-80 bg-foreground border-r border-border h-full flex flex-col overflow-hidden">
              <div className="p-4 border-b border-border">
                <h1 className="text-xl font-bold mb-4">Support Chats</h1>
                
                <div className="relative mb-4">
                  <input
                    type="text"
                    className="w-full bg-card border border-border rounded-lg px-4 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Search conversations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <FiSearch className="absolute left-3 top-2.5 text-text/50" size={18} />
                </div>
                
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-text/70">Filter by</div>
                  <button className="p-1 hover:bg-card rounded">
                    <FiFilter size={16} className="text-text/50" />
                  </button>
                </div>
                
                <div className="flex space-x-2 mb-2 overflow-x-auto pb-2 scrollbar-hide">
                  <button
                    className={`px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
                      filter === 'all' 
                        ? 'bg-primary text-white' 
                        : 'bg-card text-text/70 hover:bg-card-hover'
                    }`}
                    onClick={() => setFilter('all')}
                  >
                    All Chats
                  </button>
                  <button
                    className={`px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
                      filter === 'unread' 
                        ? 'bg-primary text-white' 
                        : 'bg-card text-text/70 hover:bg-card-hover'
                    }`}
                    onClick={() => setFilter('unread')}
                  >
                    Unread
                  </button>
                  <button
                    className={`px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
                      filter === 'expert' 
                        ? 'bg-primary text-white' 
                        : 'bg-card text-text/70 hover:bg-card-hover'
                    }`}
                    onClick={() => setFilter('expert')}
                  >
                    Expert Requested
                  </button>
                  <button
                    className={`px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
                      filter === 'assigned' 
                        ? 'bg-primary text-white' 
                        : 'bg-card text-text/70 hover:bg-card-hover'
                    }`}
                    onClick={() => setFilter('assigned')}
                  >
                    Assigned
                  </button>
                </div>
              </div>
              
              <div className="overflow-y-auto flex-grow">
                {filteredChats.length === 0 ? (
                  <div className="p-6 text-center text-text/50">
                    <FiMessageSquare size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No conversations match your filters</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {filteredChats.map((chat) => (
                      <div 
                        key={chat.id}
                        className={`p-4 hover:bg-card/50 cursor-pointer ${selectedChat === chat.id ? 'bg-card/70' : ''}`}
                        onClick={() => setSelectedChat(chat.id)}
                      >
                        <div className="flex items-start">
                          <div className="relative mr-3">
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium">
                              {chat.user.name.charAt(0)}
                            </div>
                            <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-foreground ${
                              chat.user.status === 'online' ? 'bg-green-500' : 
                              chat.user.status === 'away' ? 'bg-yellow-500' : 'bg-gray-500'
                            }`}></div>
                          </div>
                          
                          <div className="flex-grow min-w-0">
                            <div className="flex items-center justify-between">
                              <h3 className="font-medium truncate">{chat.user.name}</h3>
                              <span className="text-xs text-text/50">
                                {new Date(chat.lastMessage.timestamp).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                            
                            <div className="flex items-center justify-between mt-1">
                              <p className="text-sm text-text/70 truncate">{chat.lastMessage.text}</p>
                              {chat.unreadCount > 0 && (
                                <span className="ml-2 bg-primary text-white text-xs font-medium px-2 py-0.5 rounded-full">
                                  {chat.unreadCount}
                                </span>
                              )}
                            </div>
                            
                            {(chat.expertRequested || chat.assignedTo) && (
                              <div className="flex items-center mt-2">
                                {chat.expertRequested && !chat.assignedTo && (
                                  <span className="flex items-center text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                                    <FiAlertCircle size={12} className="mr-1" />
                                    Expert requested
                                  </span>
                                )}
                                
                                {chat.assignedTo && (
                                  <span className="flex items-center text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                                    <FiUser size={12} className="mr-1" />
                                    Assigned to {chat.assignedTo}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Chat view */}
            {selectedChat ? (
              <div className="flex-grow flex flex-col h-full overflow-hidden">
                {/* Chat header */}
                <div className="bg-foreground border-b border-border p-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="relative mr-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium">
                        {chats.find(c => c.id === selectedChat)?.user.name.charAt(0)}
                      </div>
                      <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-foreground ${
                        chats.find(c => c.id === selectedChat)?.user.status === 'online' ? 'bg-green-500' : 
                        chats.find(c => c.id === selectedChat)?.user.status === 'away' ? 'bg-yellow-500' : 'bg-gray-500'
                      }`}></div>
                    </div>
                    
                    <div>
                      <h2 className="font-semibold">
                        {chats.find(c => c.id === selectedChat)?.user.name}
                      </h2>
                      <p className="text-xs text-text/50">
                        {chats.find(c => c.id === selectedChat)?.user.status === 'online' 
                          ? 'Online' 
                          : chats.find(c => c.id === selectedChat)?.user.status === 'away'
                          ? 'Away'
                          : 'Offline'
                        }
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    {chats.find(c => c.id === selectedChat)?.expertRequested && !chats.find(c => c.id === selectedChat)?.assignedTo && (
                      <button className="bg-primary text-white text-sm px-3 py-1.5 rounded-lg mr-2">
                        Assign Expert
                      </button>
                    )}
                    
                    <button className="p-2 rounded-full hover:bg-card-hover">
                      <FiInfo size={18} className="text-text/70" />
                    </button>
                    <button className="p-2 rounded-full hover:bg-card-hover">
                      <FiMoreVertical size={18} className="text-text/70" />
                    </button>
                  </div>
                </div>
                
                {/* Chat messages */}
                <div className="flex-grow overflow-y-auto p-4 space-y-4 bg-background">
                  {currentMessages.map((message, index) => {
                    // Group messages by date
                    const showDate = index === 0 || (
                      new Date(message.timestamp).toDateString() !== 
                      new Date(currentMessages[index - 1].timestamp).toDateString()
                    );
                    
                    return (
                      <div key={message.id}>
                        {showDate && (
                          <div className="flex justify-center my-4">
                            <div className="px-3 py-1 bg-card/40 rounded-full text-xs text-text/50">
                              {new Date(message.timestamp).toLocaleDateString(undefined, {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </div>
                          </div>
                        )}
                        
                        <div className={`flex ${message.sender === 'user' ? 'justify-start' : 'justify-end'}`}>
                          <div className="flex items-start max-w-[70%]">
                            {message.sender === 'user' && (
                              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium mr-2 mt-1">
                                {chats.find(c => c.id === selectedChat)?.user.name.charAt(0)}
                              </div>
                            )}
                            
                            <div className={`p-3 rounded-lg ${
                              message.sender === 'user' 
                                ? 'bg-card' 
                                : message.sender === 'admin'
                                ? 'bg-primary/20 text-primary/90'
                                : 'bg-primary/10 text-primary'
                            }`}>
                              {message.sender === 'admin' && (
                                <div className="text-xs font-semibold mb-1">
                                  {chats.find(c => c.id === selectedChat)?.assignedTo || 'Admin'}
                                </div>
                              )}
                              <p className="whitespace-pre-wrap">{message.text}</p>
                              <div className="mt-1 flex items-center justify-end space-x-1">
                                <span className="text-xs text-text/50">
                                  {formatTime(new Date(message.timestamp))}
                                </span>
                                {message.sender !== 'user' && (
                                  <span>
                                    {message.read 
                                      ? <FiCheck size={12} className="text-primary" /> 
                                      : <FiClock size={12} className="text-text/50" />
                                    }
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Message input */}
                <div className="p-4 bg-foreground border-t border-border">
                  <div className="flex items-center">
                    <button className="p-2 rounded-full hover:bg-card-hover mr-2">
                      <FiPaperclip size={18} className="text-text/70" />
                    </button>
                    <input
                      type="text"
                      className="flex-grow bg-card border border-border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Type your message..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                    />
                    <button 
                      className="p-2 ml-2 bg-primary text-white rounded-full hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleSendMessage}
                      disabled={!messageText.trim()}
                    >
                      <FiSend size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-grow flex items-center justify-center p-8 bg-foreground">
                <div className="text-center max-w-md">
                  <FiMessageSquare size={48} className="mx-auto mb-4 text-text/30" />
                  <h2 className="text-xl font-semibold mb-2">No conversation selected</h2>
                  <p className="text-text/70">
                    Select a conversation from the sidebar to view and respond to messages.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminChats; 